import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import { GetMobilityLive } from "../../types/types";
import theme from "../../theme";
import { ArrowUpDownButton, MinusIcon } from "../../assets/icons/icons";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { useWindowSize } from "@reactuses/core";
import { memo, useEffect } from "react";
import { useI18n } from "../../locales";

const { md, lg } = theme.screen;

const LiveBoard: React.FC<{
  data: GetMobilityLive[] | undefined;
  setFold: boolean;
  mapContentsToggle?: boolean;
  setMapContentsToggle?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedInfo?: any;
}> = memo((props) => {
  const {
    data,
    setFold,
    mapContentsToggle,
    setMapContentsToggle,
    selectedInfo,
  } = props;
  const { width } = useWindowSize();
  const { t } = useI18n();

  useEffect(() => {
    if (!setMapContentsToggle) return;
    if (width < theme.screen.md) {
      // 선택된 데이터가 있는 경우
      if (selectedInfo) {
        // 모바일 사이즈에서 자동으로 활성화
        setMapContentsToggle(true);
        return;
      }

      // 선택된 데이터가 없는 경우
      if (!selectedInfo) {
        // 이미 활성화된 경우
        if (mapContentsToggle) {
          setMapContentsToggle(false);
          return;
        }
      }
    }
  }, [selectedInfo]);

  if (width <= md && mapContentsToggle) return null;

  return (
    <Wrapper setFold={setFold} mapContentsToggle={mapContentsToggle}>
      {width > md && mapContentsToggle && (
        <CustomTooltip title="활성화" active>
          <ArrowUpDownButtonWrapper
            onClick={() => {
              if (setMapContentsToggle) {
                setMapContentsToggle(false);
              }
            }}
          >
            <ArrowUpDownButton width="25px" height="25px" active />
          </ArrowUpDownButtonWrapper>
        </CustomTooltip>
      )}
      {!mapContentsToggle && (
        <>
          {width > md && (
            <CustomTooltip title="숨김" active>
              <MinusIconWrapper
                onClick={() => {
                  if (setMapContentsToggle) {
                    setMapContentsToggle(true);
                  }
                }}
              >
                <MinusIcon />
              </MinusIconWrapper>
            </CustomTooltip>
          )}
          <LiveTitle>{t.control.farmMachineryOnMap} : {data?.length}{t.dashboard.unit}</LiveTitle>
          <CountWrapper>
            <Count>
              <Circle bgColor={theme.color.pastelRed_500} />
              <span>
                {
                  data?.filter((el) => el.status !== "미운행" && el.events?.includes("차량전복"))
                    .length
                }
                {/*t.dashboard.unit*/}
              </span>
            </Count>
            <Count>
              <Circle bgColor={theme.color.pastelOrange_500} />
              <span>
                {
                  data?.filter(
                    (el) =>
                      el.event_accident === "정상" &&
                      el.status !== "미운행" && 
                      [
                        el.event_battery,
                        el.event_danger,
                        el.event_userarea,
                        el.event_emergency, // 위험운전
                        el.event_keyon, // 시동방치
                        el.event_steal, // 도난
                      ].some((el) => el !== "정상")
                  ).length
                }
                {/*t.dashboard.unit*/}
              </span>
            </Count>
            <Count>
              <Circle bgColor={theme.color.pastelGreen_500} />
              <span>
                {data?.filter((el) => 
                el.event_accident === "정상" && 
                el.status !== "미운행" && 
                !([
                  el.event_battery,
                  el.event_danger,
                  el.event_userarea,
                  el.event_emergency, // 위험운전
                  el.event_keyon, // 시동방치
                  el.event_steal, // 도난
                ].some((el) => el !== "정상"))
                ).length}{/*t.dashboard.unit*/}
              </span>
            </Count>
            <Count>
              <Circle bgColor={theme.color.neutral_400} />
              <span>
                {
                  data?.filter(
                    (el) => el.status === "미운행" || !el.status
                  ).length
                }
                {/*t.dashboard.unit*/}
              </span>
            </Count>
          </CountWrapper>
        </>
      )}
    </Wrapper>
  );
});

const utilBarLeftClose = keyframes`
from{
  left:460px;
}
to{
  left:40px;
}
`;
const utilBarLeftOpen = keyframes`
from{
  left:40px;
}
to{
  left:460px;
}
`;

const Wrapper = styled.div<{
  setFold?: boolean;
  mapContentsToggle?: boolean;
}>`
  background: ${theme.color.white};
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  position: absolute;
  z-index: 1;
  /* padding-top: 15px;
  padding-bottom: 15px; */
  top: 19px;
  padding: ${({ mapContentsToggle }) =>
    mapContentsToggle ? `10px` : `15px 0px 15px 0px`};
  transition: 200ms;

  @media (max-width: 400px) {
    font-size: small;
    padding-top: 10px;
    padding-bottom: 10px;
  }
  @media (max-width: ${md}px) {
    right: 10px;
    width: 60%;
    max-width: 263px;
  }

  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    left: 460px;
    width: ${({ mapContentsToggle }) =>
      mapContentsToggle ? `auto` : `263px`};
    ${({ setFold }) =>
      setFold
        ? css`
            animation: ${utilBarLeftClose} 0.5s ease;
          `
        : !setFold
        ? css`
            animation: ${utilBarLeftOpen} 0.5s ease;
          `
        : null}
    animation-fill-mode:both;
  }
  @media (min-width: ${lg + 1}px) {
    left: 460px;
    width: ${({ mapContentsToggle }) =>
      mapContentsToggle ? `auto` : `263px`};
    ${({ setFold }) =>
      setFold
        ? css`
            animation: ${utilBarLeftClose} 0.5s ease;
          `
        : !setFold
        ? css`
            animation: ${utilBarLeftOpen} 0.5s ease;
          `
        : null}
    animation-fill-mode:both;
  }
`;
const LiveTitle = styled.strong`
  font-weight: 700;
  font-size: 15px;
  line-height: 150%;
  text-align: center;
  letter-spacing: -0.022em;
  color: ${theme.color.neutral_800};
  display: block;
  margin: 0 auto;
  margin-bottom: 15px;
  @media (max-width: ${md}px) {
    font-size: 13px;
    margin-bottom: 5px;
  }
`;

const Circle = styled.div<{ bgColor: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ bgColor }) => bgColor};
  margin-right: 8px;
`;

const CountWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 16px;
  padding-right: 16px;
`;

const Count = styled.div`
  display: flex;
  align-items: center;
`;

const MinusIconWrapper = styled.button`
  position: absolute;
  right: 10px;
  top: 5px;
  cursor: pointer;
`;

const ArrowUpDownButtonWrapper = styled.button`
  cursor: pointer;
`;
export default LiveBoard;
